import { useState } from "react";
import { Archive, ArchiveRestore, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { ActivityLog, InternalNotes } from "./EnquiryActivity";
import { useAuth } from "./AuthContext";

export const STATUS = {
  new: { label: "New", cls: "border-orange-400/40 text-orange-300" },
  in_progress: { label: "In Progress", cls: "border-amber-400/40 text-amber-300" },
  responded: { label: "Responded", cls: "border-sky-400/40 text-sky-300" },
  closed: { label: "Closed", cls: "border-stone-500/40 text-stone-400" },
};

export const TYPE_LABEL = { quote: "Quote request", site_survey: "Site survey", consultation: "Consultation", contact: "General enquiry" };

const Row = ({ k, v }) => (v ? (
  <div className="grid grid-cols-3 gap-3 border-b border-white/10 py-3 text-sm">
    <dt className="text-stone-400">{k}</dt><dd className="col-span-2 break-words text-stone-100">{v}</dd>
  </div>
) : null);

const ArchiveControl = ({ item, onUpdated, fmt }) => {
  const { can } = useAuth();
  const [busy, setBusy] = useState(false);
  const act = async () => {
    const verb = item.archived ? "restore" : "archive";
    if (!window.confirm(item.archived ? `Restore ${item.reference} to the active inbox?` : `Archive ${item.reference}? It stays stored and can be restored at any time.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/enquiries/${item.id}/${verb}`);
      onUpdated(data);
      toast.success(item.archived ? "Enquiry restored" : "Enquiry archived");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  if (!can("enquiries.archive")) return item.archived ? <span className="rounded-full bg-amber-400/15 px-3 py-1 text-xs text-amber-200">Archived</span> : null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {item.archived && <span className="rounded-full bg-amber-400/15 px-3 py-1 text-xs text-amber-200" data-testid="admin-detail-archived-badge">Archived {fmt(item.archived_at)} by {item.archived_by}</span>}
      <button type="button" onClick={act} disabled={busy} data-testid={item.archived ? "admin-detail-restore" : "admin-detail-archive"}
        className="inline-flex h-9 items-center gap-2 rounded-full border border-white/15 px-4 text-xs text-stone-200 hover:border-orange-500">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : item.archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
        {item.archived ? "Restore" : "Archive"}
      </button>
    </div>
  );
};

export const EnquiryDetail = ({ item, onUpdated, fmt }) => {
  const [status, setStatus] = useState(item.status);
  const [notes, setNotes] = useState(item.notes ?? "");
  const [assigned, setAssigned] = useState(item.assigned_to ?? "");
  const [followUp, setFollowUp] = useState(item.follow_up_at ?? "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/enquiries/${item.id}`, { status, notes, assigned_to: assigned, follow_up_at: followUp });
      onUpdated(data);
      toast.success("Enquiry updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#141110] p-6" data-testid="admin-enquiry-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="font-mono text-sm text-orange-400">{item.reference}</p>
        <ArchiveControl item={item} onUpdated={onUpdated} fmt={fmt} />
      </div>
      <p className="mt-1 font-display text-2xl">{item.name}</p>
      <dl className="mt-6">
        <Row k="Category" v={`${TYPE_LABEL[item.type]}${item.service_specific ? " (service page)" : ""}`} />
        <Row k="Email" v={<a className="text-orange-300 underline" href={`mailto:${item.email}`}>{item.email}</a>} />
        <Row k="Phone" v={item.phone} />
        <Row k="Organisation" v={item.organisation} />
        <Row k="Industry" v={item.industry} />
        <Row k="Project location" v={item.location} />
        <Row k="Services" v={(item.services ?? []).join(", ")} />
        <Row k="Timeline" v={item.project_timeline} />
        <Row k="Budget range" v={item.budget_range} />
        <Row k="Preferred contact" v={item.preferred_contact} />
        <Row k="Submitted" v={fmt(item.created_at)} />
        <Row k="Last updated" v={fmt(item.updated_at)} />
        <Row k="Source page" v={item.source_page} />
      </dl>
      <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">Message</p>
      <p className="mt-2 whitespace-pre-wrap rounded-xl bg-black/30 p-4 text-sm leading-relaxed text-stone-200">{item.message}</p>
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <label className="block sm:col-span-1">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-detail-status"
            className="mt-2 h-11 w-full rounded-xl border border-white/10 bg-black/40 px-3 text-sm text-stone-100 outline-none focus:border-orange-500">
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </label>
        <label className="block sm:col-span-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">Summary notes</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={5000} data-testid="admin-detail-notes"
            className="mt-2 w-full rounded-xl border border-white/10 bg-black/40 p-3 text-sm text-stone-100 outline-none focus:border-orange-500" />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">Assigned to</span>
          <input value={assigned} onChange={(e) => setAssigned(e.target.value)} maxLength={120} placeholder="Team member" data-testid="admin-detail-assigned"
            className="mt-2 h-11 w-full rounded-xl border border-white/10 bg-black/40 px-3 text-sm text-stone-100 outline-none focus:border-orange-500" />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">Follow-up date</span>
          <input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} data-testid="admin-detail-followup"
            className="mt-2 h-11 w-full rounded-xl border border-white/10 bg-black/40 px-3 text-sm text-stone-100 outline-none [color-scheme:dark] focus:border-orange-500" />
        </label>
      </div>
      <button onClick={save} disabled={busy} className="tc-btn-primary mt-4 inline-flex h-11 items-center gap-2 rounded-full px-6 text-sm font-semibold" data-testid="admin-detail-save">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
      </button>
      <InternalNotes item={item} onUpdated={onUpdated} fmt={fmt} />
      <ActivityLog item={item} fmt={fmt} />
    </div>
  );
};
